import React, { useState, useEffect } from "react";
import ReusableDataTable from "../../hooks/ReusableDataTable";
import { Button, Label, Modal, TextInput } from "flowbite-react";
import { FaPen } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import useInputValidation from "../../hooks/useInputValidation";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import axios from "axios";
import { useSidebar } from "../../context/SidebarOpenContext";
import '../../style/model.css';

// Export Libraries
import jsPDF from "jspdf";
import "jspdf-autotable";

function Fabric() {
    const [AddModal, setAddModal] = useState(false);
    const [EditModal, setEditModal] = useState(false);
    const [DeleteModal, setDeleteModal] = useState(false);
    const [Input, setInput] = useState({});
    const [RawMaterial, setRawMaterial] = useState([]);
    const { errors, handleChange, resetErrors } = useInputValidation();
    const { isSidebarOpen } = useSidebar();

    // Helper for Currency Formatting
    const formatCurrency = (num, includeSymbol = true) => {
        if (includeSymbol) {
            return new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(num);
        }
        return new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num);
    };

    useEffect(() => {
        fetchRawMaterial();
    }, []);

    const onChangeValue = (e) => {
        handleChange(e);
        setInput({ ...Input, [e.target.name]: e.target.value });
    }

    const fetchRawMaterial = async () => {
        try {
            const response = await axios.get("master/fabrics");
            const RawMaterialData = response.data;
            setRawMaterial(RawMaterialData);
        } catch (error) {
            console.error("Error fetching Fabrics data:", error);
        }
    };

    const addRawMaterial = async (event) => {
        event.preventDefault();
        try {
            const res = await axios.post(
                `master/fabrics`,
                {
                    fabric_content: Input.fabric_content,
                    finish: Input.finish,
                    length: Input.length,
                    width: Input.width,
                    gsm: Input.gsm,
                    shade_no: Input.shade_no,
                    roll_no: Input.roll_no,
                    colour: Input.colour,
                    count: Input.count,
                    single_qty_rate: Input.rate || 0,
                },
                { validateStatus: () => true }
            );

            const { success, message } = res.data;
            if (success === 1) {
                Swal.fire({ icon: "success", text: "Fabric Created Successfully", timer: 2000, showConfirmButton: false });
                setAddModal(false);
                setInput({});
                fetchRawMaterial();
            } else if (success === 2) {
                Swal.fire({ icon: "warning", title: "Duplicate Roll No", text: message || "Fabric already exists", timer: 2500 });
            } else {
                Swal.fire({ icon: "error", title: "Server Error", text: message || "Something went wrong." });
            }
        } catch (error) {
            Swal.fire({ icon: "error", title: "Unexpected Error", text: "Unable to create fabric." });
        }
    };

    const UpdateRawMaterial = async (event) => {
        event.preventDefault();
        try {
            const res = await axios.put(
                `master/fabrics/${Input.id}`,
                {
                    fabric_content: Input.fabric_content,
                    finish: Input.finish,
                    length: Input.length,
                    width: Input.width,
                    gsm: Input.gsm,
                    shade_no: Input.shade_no,
                    roll_no: Input.roll_no,
                    colour: Input.colour,
                    count: Input.count,
                    single_qty_rate: Input.rate || 0,
                },
                { validateStatus: () => true }
            );

            const { success, message } = res.data;
            if (success === 1) {
                Swal.fire({ icon: "success", text: "Fabric details updated successfully!", timer: 2000, showConfirmButton: false });
                setEditModal(false);
                fetchRawMaterial();
            } else {
                Swal.fire({ icon: "error", title: "Update Failed", text: message || "Error updating data." });
            }
        } catch (error) {
            Swal.fire({ icon: "error", title: "Unexpected Error", text: "Unable to update fabric." });
        }
    };

    const deletRawMaterial = async () => {
        try {
            const res = await axios.delete(`master/fabrics/${Input.id}`);
            if (res.data.success === 1) {
                Swal.fire({ icon: "success", text: "Fabric deleted", timer: 2000, showConfirmButton: false });
                fetchRawMaterial();
                setDeleteModal(false);
            } else {
                Swal.fire({ icon: "error", text: "Server Problem. Please Try Again..." });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const data = Array.isArray(RawMaterial)
        ? RawMaterial.map((RM) => ({
            id: RM.fabric_id,
            fabric_content: RM.fabric_content,
            finish: RM.finish,
            length: RM.length,
            width: RM.width,
            gsm: RM.gsm,
            shade_no: RM.shade_no,
            roll_no: RM.roll_no,
            colour: RM.colour,
            count: RM.count,
            rate: RM.single_qty_rate || 0,
            price: RM.total_price || 0,
            option: (
                <div style={{ display: "flex" }}>
                    <FaPen className="text-amber-400 text-xl cursor-pointer" onClick={() => {
                        setEditModal(true);
                        setInput({
                            id: RM.fabric_id,
                            fabric_content: RM.fabric_content,
                            finish: RM.finish,
                            length: RM.length,
                            width: RM.width,
                            gsm: RM.gsm,
                            shade_no: RM.shade_no,
                            roll_no: RM.roll_no,
                            colour: RM.colour,
                            count: RM.count,
                            rate: RM.single_qty_rate || 0,
                        });
                    }} />
                    <MdDeleteForever className="ml-2 text-red-500 text-2xl cursor-pointer" onClick={() => { setDeleteModal(true); setInput({ id: RM.fabric_id }) }} />
                </div>
            )
        }))
        : [];

    const columns = [
        { name: "S.No", selector: (row, index) => index + 1, sortable: true },
        { name: "Fabric Content", selector: (row) => row.fabric_content, sortable: true },
        { name: "Finish", selector: (row) => row.finish, sortable: true },
        { name: "Length", selector: (row) => row.length, sortable: true },
        { name: "Width", selector: (row) => row.width, sortable: true },
        { name: "GSM", selector: (row) => row.gsm, sortable: true },
        { name: "Shade No", selector: (row) => row.shade_no, sortable: true },
        { name: "Roll No", selector: (row) => row.roll_no, sortable: true },
        { name: "Colour", selector: (row) => row.colour, sortable: true },
        { name: "Count", selector: (row) => row.count, sortable: true },
        { name: "Rate (₹)", selector: (row) => formatCurrency(row.rate), sortable: true },
        { name: "Total Price (₹)", selector: (row) => formatCurrency(row.price), width: "150px" },
        { name: "Option", selector: (row) => row.option },
    ];

    // ================= EXPORT LOGIC (NO VENDOR) =================

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return `${d}-${m}-${y}`;
    };

    const today = formatDate(new Date());
    const totalCount = data.reduce((sum, row) => sum + (Number(row.count) || 0), 0);
    const overallTotalPrice = data.reduce((sum, row) => sum + (Number(row.price) || 0), 0);
    const filename = "Fabric_Stock_Report";

    const exportHeaders = ["S.No", "Fabric Content", "Finish", "Length", "Width", "GSM", "Shade No", "Roll No", "Colour", "Count", "Rate", "Total Price"];

    const getExportData = () => {
        return data.map((row, index) => [
            index + 1,
            row.fabric_content || "-",
            row.finish || "-",
            row.length || "-",
            row.width || "-",
            row.gsm || "-",
            row.shade_no || "-",
            row.roll_no || "-",
            row.colour || "-",
            row.count || "0",
            formatCurrency(row.rate, false),
            formatCurrency(row.price, false)
        ]);
    };

    const handlePrint = () => {
        const win = window.open('', '_blank');
        const rows = data.map((row, index) => `
            <tr>
                <td>${index + 1}</td>
                <td>${row.fabric_content || "-"}</td>
                <td>${row.finish || "-"}</td>
                <td>${row.length || "-"}</td>
                <td>${row.width || "-"}</td>
                <td>${row.gsm || "-"}</td>
                <td>${row.shade_no || "-"}</td>
                <td>${row.roll_no || "-"}</td>
                <td>${row.colour || "-"}</td>
                <td>${row.count || "0"}</td>
                <td>${formatCurrency(row.rate, false)}</td>
                <td>${formatCurrency(row.price, false)}</td>
            </tr>
        `).join("");

        win.document.write(`
            <html>
                <head>
                    <title>${filename.replace(/_/g, ' ')}</title> 
                    <style>
                        @page { size: landscape; margin: 10mm; }
                        body { font-family: 'Segoe UI', sans-serif; color: #333; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th { background-color: #f2f2f2; border: 1px solid #ccc; padding: 10px; font-size: 11px; }
                        td { border: 1px solid #ccc; padding: 10px; font-size: 12px; }
                        .total-row { background-color: #eee; font-weight: bold; }
                    </style>
                </head>
                <body>
                    <div style="text-align:center;">
                        <h2>${filename.replace(/_/g, ' ')}</h2>
                        <p>Date Generated: ${today}</p>
                    </div>
                    <table>
                        <thead><tr>${exportHeaders.map(h => `<th>${h}</th>`).join('')}</tr></thead>
                        <tbody>
                            ${rows}
                            <tr class="total-row">
                                <td colspan="9" style="text-align: right;">OVERALL TOTAL:</td>
                                <td>${totalCount}</td>
                                <td>-</td>
                                <td>${formatCurrency(overallTotalPrice, false)}</td>
                            </tr>
                        </tbody>
                    </table>
                </body>
            </html>
        `);
        win.document.close();
        win.focus();
        win.print();
        win.close();
    };

    const downloadPDF = () => {
        const doc = new jsPDF("l", "pt", "a4");
        doc.setFontSize(16);
        doc.text(`${filename.replace(/_/g, ' ')}`, 40, 45);
        doc.setFontSize(10);
        doc.text(`Date: ${today}`, 730, 45);

        const tableRows = getExportData();
        tableRows.push([
            { content: 'OVERALL TOTAL DETAILS', colSpan: 9, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: totalCount.toString(), styles: { fontStyle: 'bold' } },
            { content: '-', styles: { fontStyle: 'bold' } },
            { content: formatCurrency(overallTotalPrice, false), styles: { fontStyle: 'bold' } }
        ]);

        doc.autoTable({
            head: [exportHeaders],
            body: tableRows,
            startY: 65,
            theme: 'grid',
            styles: { fontSize: 8 },
            headStyles: { fillColor: [40, 40, 40] }
        });
        doc.save(`${filename}_${today}.pdf`);
    };

    const downloadCSV = () => {
        const rows = getExportData().map(row => row.map(cell => `"${cell}"`));
        rows.push(["", "", "", "", "", "", "", "", `"OVERALL TOTAL"`, `"${totalCount}"`, `"-"`, `"${formatCurrency(overallTotalPrice, false)}"`]);
        const csvContent = "\uFEFF" + [exportHeaders.map(h => `"${h}"`), ...rows].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${filename}_${today}.csv`);
        link.click();
    };

    return (
        <div className={` ${isSidebarOpen ? "p-4 sm:ml-64 bg-content-bg min-h-screen " : "p-4  bg-content-bg min-h-screen"}  `} >
            <div className="p-4 rounded-lg dark:border-gray-700  mt-14">
                <div className="p-4 rounded-lg shadow bg-white">
                    <div className="flex flex-wrap justify-between items-center mb-4 gap-4">
                        <Button className="bg-icon-color text-sidebar-color drop-shadow-md font-bold" onClick={() => { setAddModal(true); setInput({}); }} >
                            Add Fabrics
                        </Button>
                        <div className="flex gap-2">
                            <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-black text-white rounded-lg transition-all shadow-md font-medium text-sm">🖨️ Print</button>
                            <button onClick={downloadPDF} className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg transition-all shadow-md font-medium text-sm">📄 PDF</button>
                            <button onClick={downloadCSV} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all shadow-md font-medium text-sm">📥 CSV</button>
                        </div>
                    </div>

                    <ReusableDataTable title="Fabrics List" columns={columns} data={data} />
                </div>
            </div>

            {/* ADD MODAL */}
            <Modal show={AddModal} onClose={() => { setAddModal(false); resetErrors(); }} popup>
                <Modal.Header />
                <Modal.Body>
                    <div className="space-y-6">
                        <h3 className="text-xl font-medium text-gray-900">Add Fabric</h3>
                        <form onSubmit={addRawMaterial} className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="fabric_content" value="Fabric Content" />
                                <TextInput name="fabric_content" id="fabric_content" required onChange={onChangeValue} value={Input.fabric_content || ''} color={errors.fabric_content && "failure"} />
                            </div>
                            <div>
                                <Label htmlFor="finish" value="Finish" />
                                <TextInput id="finish" name="finish" type="text" onChange={onChangeValue} value={Input.finish || ''} required />
                            </div>
                            <div>
                                <Label htmlFor="length" value="Length" />
                                <TextInput id="length" name="length" type="text" onChange={onChangeValue} value={Input.length || ''} required />
                            </div>
                            <div>
                                <Label htmlFor="width" value="Width" />
                                <TextInput id="width" name="width" type="text" onChange={onChangeValue} value={Input.width || ''} required />
                            </div>
                            <div>
                                <Label htmlFor="gsm" value="GSM" />
                                <TextInput id="gsm" name="gsm" type="number" onChange={onChangeValue} value={Input.gsm || ''} required />
                            </div>
                            <div>
                                <Label htmlFor="shade_no" value="Shade No" />
                                <TextInput id="shade_no" name="shade_no" type="text" onChange={onChangeValue} value={Input.shade_no || ''} required />
                            </div>
                            <div>
                                <Label htmlFor="roll_no" value="Roll No" />
                                <TextInput id="roll_no" name="roll_no" type="text" onChange={onChangeValue} value={Input.roll_no || ''} required />
                            </div>
                            <div>
                                <Label htmlFor="colour" value="Colour" />
                                <TextInput id="colour" name="colour" type="text" onChange={onChangeValue} value={Input.colour || ''} required />
                            </div>
                            <div>
                                <Label htmlFor="count" value="Count" />
                                <TextInput id="count" name="count" type="text" onChange={onChangeValue} value={Input.count || ''} required />
                            </div>
                            <div>
                                <Label htmlFor="rate" value="Rate per Unit (₹)" />
                                <TextInput id="rate" name="rate" type="number" step="0.01" onChange={onChangeValue} value={Input.rate || ''} required />
                            </div>
                            <div className="pt-2 w-full flex justify-center col-span-2">
                                <Button className="bg-icon-color text-sidebar-color" type="submit">Submit</Button>
                                <Button className="ml-2" color="gray" onClick={() => setAddModal(false)}>Cancel</Button>
                            </div>
                        </form>
                    </div>
                </Modal.Body>
            </Modal>

            {/* EDIT MODAL */}
            <Modal show={EditModal} onClose={() => { setEditModal(false); resetErrors(); }} popup>
                <Modal.Header />
                <Modal.Body>
                    <div className="space-y-6">
                        <h3 className="text-xl font-medium text-gray-900">Update Fabric</h3>
                        <form onSubmit={UpdateRawMaterial} className="grid grid-cols-2 gap-4">
                            <div>
                                <Label value="Fabric Content" />
                                <TextInput name="fabric_content" onChange={onChangeValue} value={Input.fabric_content} required />
                            </div>
                            <div>
                                <Label value="Finish" />
                                <TextInput name="finish" onChange={onChangeValue} value={Input.finish} required />
                            </div>
                            <div>
                                <Label value="Length" />
                                <TextInput name="length" type="number" onChange={onChangeValue} value={Input.length} required />
                            </div>
                            <div>
                                <Label value="Width" />
                                <TextInput name="width" type="number" onChange={onChangeValue} value={Input.width} required />
                            </div>
                            <div>
                                <Label value="GSM" />
                                <TextInput name="gsm" type="number" onChange={onChangeValue} value={Input.gsm} required />
                            </div>
                            <div>
                                <Label value="Shade No" />
                                <TextInput name="shade_no" onChange={onChangeValue} value={Input.shade_no} required />
                            </div>
                            <div>
                                <Label value="Roll No" />
                                <TextInput name="roll_no" onChange={onChangeValue} value={Input.roll_no} required />
                            </div>
                            <div>
                                <Label value="Colour" />
                                <TextInput name="colour" onChange={onChangeValue} value={Input.colour} required />
                            </div>
                            <div>
                                <Label value="Count" />
                                <TextInput name="count" onChange={onChangeValue} value={Input.count} required />
                            </div>
                            <div>
                                <Label value="Rate (₹)" />
                                <TextInput name="rate" type="number" step="0.01" onChange={onChangeValue} value={Input.rate} required />
                            </div>
                            <div className="pt-2 w-full flex justify-center col-span-2">
                                <Button className="bg-icon-color text-sidebar-color" type="submit">Update</Button>
                                <Button className="ml-2" color="gray" onClick={() => setEditModal(false)}>Cancel</Button>
                            </div>
                        </form>
                    </div>
                </Modal.Body>
            </Modal>

            {/* DELETE MODAL */}
            <Modal show={DeleteModal} size="md" onClose={() => setDeleteModal(false)} popup>
                <Modal.Header />
                <Modal.Body>
                    <div className="text-center">
                        <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400" />
                        <h3 className="mb-5 text-lg font-normal text-gray-500">Are you sure you want to delete this Fabric?</h3>
                        <div className="flex justify-center gap-4">
                            <Button color="failure" onClick={() => deletRawMaterial()}>Yes</Button>
                            <Button color="gray" onClick={() => setDeleteModal(false)}>No</Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default Fabric;